// Public page — consume the token+email from the reset email link and set
// a new password. Token + email come from the URL search params.

import { useState, type JSX, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FormField } from "../components/FormField.js";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "") + "/hcm/api/v1";

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get("token") ?? "";
  const email = params.get("email") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm,    setConfirm]    = useState("");
  const [error,      setError]      = useState<string | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [done,       setDone]       = useState(false);

  const linkInvalid = !token || !email;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, email, newPassword }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(typeof body.error === "string" ? body.error : "Could not reset password.");
        setLoading(false);
        return;
      }
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch {
      setError("Unable to connect. Check your internet connection.");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-2xl font-bold text-navy-700">HCM</span>
          <p className="mt-1 text-sm text-slate-500">Home Care Management</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Set a new password</h1>

          {done ? (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Password updated. Redirecting to sign in…
            </p>
          ) : linkInvalid ? (
            <>
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-6">
                This reset link is incomplete. Request a new one from the sign-in page.
              </p>
              <Link to="/forgot-password" className="block w-full text-center h-10 leading-10
                bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800">
                Request a new link
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-6">
                Resetting password for <strong>{email}</strong>.
              </p>
              {error && (
                <div role="alert" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}
              <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4" noValidate>
                <FormField
                  id="new-password"
                  type="password"
                  label="New password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                />
                <FormField
                  id="confirm-password"
                  type="password"
                  label="Confirm password"
                  value={confirm}
                  onChange={setConfirm}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-10 bg-navy-700 text-white text-sm font-medium rounded-lg hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {loading ? "Resetting…" : "Reset password"}
                </button>
                <Link to="/login" className="block w-full text-center text-sm text-slate-500 hover:text-slate-700">
                  ← Back to sign in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
